import { useEffect } from 'react'
import { useImageZoomViewer } from '../hooks/useImageZoomViewer'
import { ZoomModalHeader } from './product-image-zoom/ZoomModalHeader'
import { ZoomThumbnails } from './product-image-zoom/ZoomThumbnails'
import { ZoomViewport } from './product-image-zoom/ZoomViewport'
import { useZoomGallery } from './product-image-zoom/useZoomGallery'

type Props = {
  open: boolean
  images: string[]
  activeImage: string
  imageAlt: string
  onSelectImage: (imageUrl: string) => void
  onClose: () => void
}

const ProductImageZoomModal = ({
  open,
  images,
  activeImage,
  imageAlt,
  onSelectImage,
  onClose,
}: Props) => {
  const {
    containerRef,
    scale,
    offset,
    isDragging,
    isDarkMode,
    applyScale,
    handleWheel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
  } = useImageZoomViewer({ open })
  const { gallery, resolvedImage } = useZoomGallery(images, activeImage)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open || !resolvedImage) return null

  return (
    <div
      className={`fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6 ${
        isDarkMode ? 'bg-slate-950/80' : 'bg-slate-900/50'
      }`}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`flex h-full max-h-[92vh] w-full max-w-6xl flex-col rounded-2xl border shadow-2xl ${
          isDarkMode ? 'border-slate-700 bg-slate-900/95' : 'border-slate-200 bg-white/95'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <ZoomModalHeader isDarkMode={isDarkMode} scale={scale} onApplyScale={applyScale} onClose={onClose} />

        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3 sm:p-4">
          <ZoomViewport
            containerRef={containerRef}
            isDarkMode={isDarkMode}
            isDragging={isDragging}
            scale={scale}
            offset={offset}
            imageUrl={resolvedImage}
            imageAlt={imageAlt}
            onApplyScale={applyScale}
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Doppio click per zoom rapido. Usa drag/pinch quando zoom &gt; 1x.
            </p>
            <button
              type="button"
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                isDarkMode
                  ? 'border-slate-600 text-slate-100 hover:border-slate-500'
                  : 'border-slate-300 text-slate-700 hover:border-slate-400'
              }`}
              onClick={() => applyScale(1)}
            >
              Reimposta vista
            </button>
          </div>

          <ZoomThumbnails
            gallery={gallery}
            resolvedImage={resolvedImage}
            isDarkMode={isDarkMode}
            onSelectImage={(url) => {
              applyScale(1)
              onSelectImage(url)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default ProductImageZoomModal
